/**
 * Account identity — who this customer is across our systems: how they signed
 * in (LINE / email), their avatar + name, and which storefront they belong to.
 *
 * Login identity comes from the Customer's `portal_users` child table (already
 * in formData — no extra fetch). Each row's `owner` preserves the canonical
 * LINE user id (mixed-case `U…`); `user` is the lowercased Frappe User. A
 * `line_<id>@{tenant}.storefront` owner = LINE login; anything else = email.
 *
 * Exposes small composable pieces (`AccountIdentity`, `OpenInInboxButton`) so
 * the combined Account + Financial summary box can lay them out freely.
 */

import { useState } from "react";
import { toast } from "sonner";
import { Store, MessageCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchCustomerConversation } from "@/features/customers/api/customerConversation";
import { useChatPanelStore } from "@/lib/store/chatPanelStore";
import { useStoreList } from "@/hooks/useStoreList";

export interface PortalUserRow {
  user?: string;
  owner?: string;
}

export interface LoginIdentity {
  method: "line" | "email";
  /** Canonical login email/identifier (owner preserves LINE hex case). */
  identifier: string;
  /** LINE user id (mixed-case, capital U) — present for LINE logins. */
  lineUserId?: string;
}

/** Parse distinct login identities from a Customer's portal_users rows. */
export function parseLoginIdentities(portalUsers?: PortalUserRow[]): LoginIdentity[] {
  const out: LoginIdentity[] = [];
  const seen = new Set<string>();
  for (const pu of portalUsers ?? []) {
    // `owner` keeps the canonical LINE hex case (QF-893); fall back to `user`.
    const identifier = (pu.owner || pu.user || "").trim();
    if (!identifier) continue;
    const isLine = /^line_/i.test(identifier);
    const lineUserId = isLine ? identifier.match(/^line_([^@]+)@/i)?.[1] : undefined;
    const key = isLine ? `line:${(lineUserId || "").toLowerCase()}` : `email:${identifier.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ method: isLine ? "line" : "email", identifier, lineUserId });
  }
  return out;
}

function LineBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-[#06C755] px-1.5 py-0.5 text-[10px] font-bold text-white">
      LINE
    </span>
  );
}

/**
 * Source cluster — where this customer came from: login method (LINE / Email) +
 * storefront. A single inline row (no label), so it reads as one calm identity
 * line. The avatar + name now live in the page header, so this omits them.
 */
export function AccountIdentity({
  portalUsers,
  storefrontKey,
}: {
  customer: string;
  customerName?: string;
  portalUsers?: PortalUserRow[];
  storefrontKey?: string;
}) {
  const { data: stores } = useStoreList();

  const logins = parseLoginIdentities(portalUsers);
  const primary = logins[0];
  const storeName = stores?.find((s) => s.storefront_key === storefrontKey)?.label;

  return (
    <div className="flex min-w-0 items-center gap-2">
      {primary?.method === "line" ? (
        <LineBadge />
      ) : primary ? (
        <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Email
        </span>
      ) : null}
      <div className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">
        <Store className="h-3.5 w-3.5 shrink-0" />
        <span className={cn("truncate", !storeName && !storefrontKey && "italic")}>
          {storeName || storefrontKey || "No store"}
        </span>
      </div>
    </div>
  );
}

/**
 * "Open in Inbox" — opens this customer's LINE conversation in the floating
 * Chatwoot panel (AppRailRight). Renders nothing for non-LINE customers.
 */
export function OpenInInboxButton({
  lineUserId,
  className,
  label = "Open in Inbox",
}: {
  lineUserId?: string;
  className?: string;
  label?: string;
}) {
  const openChatPanel = useChatPanelStore((s) => s.openConversation);
  const [opening, setOpening] = useState(false);

  if (!lineUserId) return null;

  const openInInbox = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const res = await fetchCustomerConversation({ lineUserId });
      if (res.success && res.conversation_id != null && res.account_id != null) {
        openChatPanel(res.account_id, res.conversation_id);
      } else if (res.reason === "chatwoot_not_provisioned") {
        toast.info("This store isn't connected to the inbox yet");
      } else {
        toast.info("No inbox conversation found for this customer");
      }
    } catch {
      toast.error("Couldn't open the inbox");
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={openInInbox}
      disabled={opening}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border/60 px-2.5 py-1.5 text-[12px] font-medium text-primary transition-colors hover:bg-muted/50 disabled:opacity-60",
        className,
      )}
    >
      {opening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}
